import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CoreTracking } from "../../../../models/CoreTracking";
import ModalComponent from '../../../misc/ModalComponent';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { InactivateGoogleUserTracking, UpgradeGoogleUserTracking } from "../../../../store/TrackingOperationAction";
import { getModulesByTypeName } from "../../../../store/ModuleAction";
import { EmptyRequestState } from "../../../../models/RequestState";
import { CoreModuleRef } from "../../../../models/CoreModels";
import StatusCode from "../../../../util/StatusCode";
import Loading from "../../../misc/Loading";
import CoreApiError from "../../../misc/CoreApiError";
import { useTranslation } from 'react-i18next';
import { RootState } from "../../../../store/store";
import { RequestState } from "../../../../models/RequestState";
import SelectInput from "../../../misc/SelectInput";


interface UpgradeGoogleUserTrackingWizardProps {
    tracking: CoreTracking
    close: (success: boolean) => void
}

const UpgradeGoogleUserTrackingWizard: React.FC<UpgradeGoogleUserTrackingWizardProps> = (props) => {
    const dispatch = useDispatch();
    const [wizardStep, setWizardStep] = useState<'confirm' | 'done'>('confirm');
    const upgradeGoogleUserTracking = useSelector<RootState, EmptyRequestState>(state => state.trackingOperation.upgradeGoogleUserTracking)
    const modules = useSelector<RootState, RequestState<CoreModuleRef[]>>(state => state.module.modulesByTypeName);
    const [selectedModule, setSelectedModule] = useState<string | undefined>(undefined);
    const [isInvalid, setIsInvalid] = useState<boolean>(true);
    const [hasInteracted, setHasInteracted] = useState<boolean>(false);
    const { t } = useTranslation(['schoolAdministration', 'common']);
    const TypeName = "GoogleUserExport";

    useEffect(() => {
        getModulesByTypeName(TypeName)(dispatch);
    }, [dispatch]);


    const requestData = <T,>(requestState: RequestState<T[]>): T[] => {
        if (requestState.code === StatusCode.COMPLETE && 'data' in requestState && requestState.data) {
            return requestState.data;
        }
         return [];
    };


    const updateSelected = (value?: string) => {
        setHasInteracted(true);
        if(value !== undefined && value !== ''){
            setSelectedModule(value)
            setIsInvalid(false);
        }
        else {
            setSelectedModule(undefined)
            setIsInvalid(true)
        }
    };   

    const confirmInput: React.MouseEventHandler<HTMLButtonElement> = (_) => {
        if(selectedModule !== undefined){
            UpgradeGoogleUserTracking(props.tracking.trackingId, selectedModule, false)(dispatch);
            setWizardStep('done');
        }
        else{
            setIsInvalid(true);
        }
    };

    const close = (success: boolean) => {
        setWizardStep('confirm');
        props.close(success);
    };

    const showInvalidFeedback: boolean = isInvalid && hasInteracted;

    switch (wizardStep) {
        case 'confirm': {
            return <ModalComponent
                disabled={isInvalid}
                isOpen={true}
                toggleModal={() => close(false)}
                header={t('trackingOperations.google.upgrade.modalTitle', {ns: 'schoolAdministration'})}
                update={confirmInput}
                confirm={t('action.confirm', {ns: 'common'})}
                cancel={t('action.cancel', {ns: 'common'})}>
                <p><FontAwesomeIcon title={t('status.warning', {ns: 'common'})} color="#fc1" icon={['fas', 'triangle-exclamation']} /> {t('trackingOperations.google.upgrade.warning', {ns: 'schoolAdministration', SourceSystemId: props.tracking.sourceSystemId, ObjectType: props.tracking.sourceObjectType})}</p>
                    <SelectInput
                        items={requestData(modules).map((m: CoreModuleRef) => ({
                        value: m.id, text: `${m.title}` }))}
                        onChange={(name, value) => updateSelected(value)}
                        name='moduleTypeName'
                        selected={selectedModule}
                        defaultText={t('trackingOperations.google.upgrade.defaultText', {ns: 'schoolAdministration'})}
                        invalid = {showInvalidFeedback}
                        invalidFeedback={t('trackingOperations.google.upgrade.invalidFeedback', {ns: 'schoolAdministration'})}
                    />
            </ModalComponent>
        }
        case 'done': {

            if (upgradeGoogleUserTracking.code === StatusCode.COMPLETE) {
                return <ModalComponent
                    isOpen={true}
                    toggleModal={() => close(true)}
                    header={t('trackingOperations.google.upgrade.modalTitle', {ns: 'schoolAdministration'})}
                    cancel={t('action.close', {ns: 'common'})}>{t('trackingOperations.general.changeCompleted', {ns: 'schoolAdministration'})}</ModalComponent>
            } else if (upgradeGoogleUserTracking.code === StatusCode.ERROR) {
                return <ModalComponent
                    isOpen={true}
                    toggleModal={() => close(false)}
                    header={t('trackingOperations.google.upgrade.modalTitle', {ns: 'schoolAdministration'})}
                    cancel={t('action.cancel', {ns: 'common'})}><CoreApiError  requestState={upgradeGoogleUserTracking} /></ModalComponent>
            } else {
                return <ModalComponent
                    isOpen={true}
                    toggleModal={() => close(false)}
                    header={t('trackingOperations.google.upgrade.modalTitle', {ns: 'schoolAdministration'})}
                    cancel={t('action.cancel', {ns: 'common'})}>
                    <Loading />
                </ModalComponent>;
            }
        }
    }
}

export default UpgradeGoogleUserTrackingWizard;
